import React, { useState, useEffect } from "react";
import "./CompanyDashboard.css";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Building,
  Folder,
  Zap,
  Edit,
  Trash2,
  Search,
} from "lucide-react";
import AddCampaignModal from "./AddCampaignModal";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import CompanyEditModel from "./CompanyEditModal";
import AuthService from "../../services/Auth.service";
import { toast } from "react-hot-toast";

const CompanyDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [expandedCampaignId, setExpandedCampaignId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const [isCampaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loading, setLoading] = useState(false);

  const postsPerPage = 10; //  Added missing variable

  //  Fetch Company List
  const fetchDetail = async (page = 1, size = postsPerPage) => {
    try {
      setLoading(true);
      const res = await AuthService.getCompanyList({ page, size });

      if (res.status === 200 && res.data) {
        setCompanies(res.data.data || []);
      } else {
        toast.error("Failed to fetch company list");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, []);

  //  Filter companies
  const filteredCompanies = companies.filter((company) =>
    company.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddCompany = () => {
    setCompanyToEdit(null);
    setShowCompanyModal(true);
  };

  const handleEditCompany = (company) => {
    setCompanyToEdit({
      id: company.id,
      name: company.company_name,
      description: company.company_description,
      call_minute_limit: company.call_minute_limit || 0,
      status: company.status !== undefined ? company.status : true,
    });
    setShowCompanyModal(true);
  };

 const handleSaveCompany = async (data) => {
  try {
    let res;

    // --------- EDIT MODE ---------
    if (data.id) {
      const payload = {
        name: data.name || "",
        description: data.description || "",
        call_minute_limit: data.call_minute_limit,
        status: data.status,
      };

      // Update locally
      setCompanies((prev) =>
        prev.map((c) => (c.id === data.id ? { ...c, ...payload } : c))
      );

      // API Call
      res = await AuthService.updateCompany(data.id, payload);

      if (res.status === 200) {
        toast.success("Company updated successfully");
      }
      else{
         toast.error("Error on company Upadte");
      }
    }

    // --------- ADD MODE ---------
    else {
      const payload = {
        name: data.name,
        email: data.email,
        password: data.password,
        confirm_password: data.confirm_password,
        description: data.description,
      };

      res = await AuthService.addNewCompany(payload);

      if (res.status === 200 || res.status === 201) {
        toast.success("Company added successfully");
      } else {
        toast.error("Failed to add company");
      }
    }
  } catch (error) {
    console.error("Error saving company:", error);
    toast.error("Error saving company");
  } finally {
    setShowCompanyModal(false);
  }
};

  const toggleCompany = (id) => {
    setExpandedCompanyId(expandedCompanyId === id ? null : id);
    setExpandedCampaignId(null);
  };

  const toggleCampaign = (id) => {
    setExpandedCampaignId(expandedCampaignId === id ? null : id);
  };

  const handleAddCampaign = (companyId) => {
    setSelectedCompanyId(companyId);
    setCampaignToEdit(null);
    setCampaignModalOpen(true);
  };

  const handleEditCampaign = (companyId, campaign) => {
    setSelectedCompanyId(companyId);
    setCampaignToEdit(campaign);
    setCampaignModalOpen(true);
  };

const handleSaveCampaign = async (data) => {
  try {
    const formatTime = (t) => (t ? `${t}` : "00:00:00");
    let res;
   
    if (campaignToEdit) {
       const selectedCompany = companies.find(
        (c) => c.id === selectedCompanyId
      );
      const payload = {
        company_id: selectedCompanyId,
        company_name: selectedCompany?.company_name || "",
        campaign_name: data.campaign_name,
        campaign_description: data.campaign_description || "",
        start_time: formatTime(data.start_time),
        end_time: formatTime(data.end_time),
        status: data.status,
        script: data.script || "",
        numbers: data.numbers || [],
        stt_type: data.stt_type || "",
        llm_type: data.llm_type || "",
        tts_type: data.tts_type || "",
        timeout: Number(data.timeout) || 0,
        dialer: data.dialer || "",
        language_mode: data.language_mode,
        language: data.language,
        dispositions: data.dispositions || [],
        call_transfer: Boolean(data.call_transfer),
        call_transfer_number: data.call_transfer ? data.call_transfer_number : "",
        call_transfer_threshold: data.call_transfer_threshold || 0,
        service_id:data.service_id
      };

      res = await AuthService.updateCampaign(
        campaignToEdit.campaign_id,
        payload
      );

      if (res.status === 200) {

        toast.success("Campaign updated successfully");
        setCompanies((prev) =>
          prev.map((company) =>
            company.id === selectedCompanyId
              ? {
                  ...company,
                  campaigns: company.campaigns.map((camp) =>
                    camp.campaign_id === campaignToEdit.campaign_id
                      ? { ...camp, ...payload }
                      : camp
                  ),
                }
              : company
          )
        );
      } else {
        toast.error("Failed to update campaign");
      }
    }
    else {
      const selectedCompany = companies.find(
        (c) => c.id === selectedCompanyId
      );

      const payload = {
        company_id: selectedCompanyId,
        company_name: selectedCompany?.company_name || "",
        campaign_name: data.campaign_name,
        campaign_description: data.campaign_description || "",
        start_time: formatTime(data.start_time),
        end_time: formatTime(data.end_time),
        status: data.status || "active",
        script: data.script || "",
        numbers: data.numbers || [],
        stt_type: data.stt_type || "",
        llm_type: data.llm_type || "",
        tts_type: data.tts_type || "",
        timeout: Number(data.timeout) || 0,
        dialer: data.dialer,
        language_mode: data.language_mode,
        language: data.language,
        dispositions: data.dispositions || [],
        call_transfer: Boolean(data.call_transfer),
        call_transfer_number: data.call_transfer ? data.call_transfer_number : "",
      };

      res = await AuthService.createNewCampaign(payload);

      if (res.status === 200 || res.status === 201) {
        toast.success("Campaign created successfully");
      } else {
        toast.error("Failed to create campaign");
      }
    }
  } catch (error) {
    console.error("Error saving campaign:", error);
    toast.error("Error while saving campaign");
  } finally {
    setCampaignModalOpen(false);
  }
};




// Delete Campaign Trigger
const handleDeleteCampaign = (companyId, campaignId) => {
  const company = companies.find((c) => c.id === companyId);
  const target = company?.campaigns.find(
    (camp) => camp.campaign_id === campaignId
  );
  setDeleteTarget({
    type: "campaign",
    companyId,
    campaignId,
    name: target?.campaign_name,
  });
  setShowDeleteModal(true);
};

// Delete Company Trigger
const handleDeleteCompany = (companyId) => {
  const company = companies.find((c) => c.id === companyId);
  setDeleteTarget({
    type: "company",
    companyId,
    name: company?.company_name,
  });
  setShowDeleteModal(true);
};


const handleConfirmDelete = async () => {
  try {
    const { type, companyId, campaignId } = deleteTarget;
    let res;

    if (type === "company") {
      res = await AuthService.deleteCompany(companyId);
    } else if (type === "campaign") {
      res = await AuthService.deleteCampaign(campaignId);
    }

    if (res.status === 200 || res.status === 204) {
      toast.success(
        type === "company"
          ? "Company deleted successfully"
          : "Campaign deleted successfully"
      );

      // 🧹 Update UI
      if (type === "company") {
        setCompanies((prev) => prev.filter((c) => c.id !== companyId));
      } else {
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === companyId
              ? {
                  ...c,
                  campaigns: c.campaigns.filter(
                    (camp) => camp.campaign_id !== campaignId
                  ),
                }
              : c
          )
        );
      }
    } else {
      toast.error("Failed to delete");
    }
  } catch (error) {
    console.error(error);
    toast.error("Error deleting");
  } finally {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  }
};


  return (
    <div>
      {/* --- Search + Add Company --- */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 12px",
          marginBottom: "10px",
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <Search
            size={16}
            color="#888"
            style={{
              position: "absolute",
              left: "10px",
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder="Search company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 12px 8px 32px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              width: "250px",
              fontSize: "0.9rem",
              outline: "none",
            }}
          />
        </div>

        <button className="addCampaignBtn" onClick={handleAddCompany}>
          <Plus size={16} /> Add Company
        </button>
      </div>

      {/* --- Company List --- */}
      {loading ? (
        <p style={{ textAlign: "center", marginTop: "20px" }}>Loading companies...</p>
      ) : filteredCompanies.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: "20px" }}>No companies found.</p>
      ) : (
        filteredCompanies.map((company) => {
          const isCompanyExpanded = expandedCompanyId === company.id;
          return (
            <div key={company.id} className="dashboardContainer">
              <header
                className="companyHeader"
                onClick={() => toggleCompany(company.id)}
                style={{ cursor: "pointer" }}
              >
                <div className="headerLeft">
                  <div className="companyIconWrapper">
                    <Building size={24} color="#3B82F6" />
                  </div>
                  <div className="headerInfo">
                    <h1>{company.company_name}</h1>
                    {company.company_description && (
                      <p>{company.company_description}</p>
                    )}
                  </div>
                </div>
                <div className="headerRight">
                  <span className="badge-1">
                    {company.campaigns?.length || 0} campaigns
                  </span>
                  {(company.campaigns?.filter(
                    (campaign) => campaign.status === "pending"
                  ).length || 0) > 0 && (
                    <span className="badge-pending">
                      {
                        company.campaigns?.filter(
                          (campaign) => campaign.status === "pending"
                        ).length
                      } PENDING
                    </span>
                  )}
                  <button
                    className="iconButton"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCompany(company);
                    }}
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    className="iconButton"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCompany(company.id);
                    }}
                  >
                    <Trash2 size={20} color="red" />
                  </button>

                  <button className="iconButton">
                    {isCompanyExpanded ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                </div>
              </header>

              {/* --- Campaigns Section --- */}
              {isCompanyExpanded && (
                <section className="campaignsSection">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <h5>Campaigns</h5>
                    <button
                      className="addCampaignBtn"
                      onClick={() => handleAddCampaign(company.id)}
                    >
                      <Plus size={16} /> Add Campaign
                    </button>
                  </div>

                  {company.campaigns?.map((campaign) => {
                    const isCampaignExpanded =
                      expandedCampaignId === campaign.campaign_id;

                    return (
                      <div key={campaign.campaign_id} className="campaignItem">
                        <div
                          className="dashboardHeader"
                          onClick={() => toggleCampaign(campaign.campaign_id)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="headerLeft">
                            <div className="campaignIconWrapper">
                              <Folder size={24} color="#9333EA" />
                            </div>
                            <div className="headerInfo">
                              <div className="campaignTitle">
                                {campaign.campaign_name}
                                <span
                                  className={`statusBadge ${campaign.status}`}
                                >
                                  {campaign.status}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="headerRight">
                            <span className="statItem">
                              <Zap size={16} /> {campaign.tokens} tokens
                            </span>
                            <span className="statItem cost">
                              ₹{campaign.cost}
                            </span>

                            <button
                              className="iconButton"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCampaign(company.id, campaign);
                              }}
                              title="Edit Campaign"
                            >
                              <Edit size={20} />
                            </button>
                           <button
                                className="iconButton"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCampaign(company.id, campaign.campaign_id);
                                }}
                              >
                                <Trash2 size={20} color="red" />
                              </button>

                            <button className="iconButton">
                              {isCampaignExpanded ? (
                                <ChevronUp size={20} />
                              ) : (
                                <ChevronDown size={20} />
                              )}
                            </button>
                          </div>
                        </div>

                        {isCampaignExpanded && (
                          <table className="scriptTable">
                            <thead>
                              <tr>
                                <th>STT Type</th>
                                <th>TTS Type</th>
                                <th>LLM Type</th>
                                <th>Script</th>
                                <th>Start-End Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {campaign.config?.map((cfg, index) => (
                                <tr key={index}>
                                  <td>{cfg.stt_type}</td>
                                  <td>{cfg.tts_type}</td>
                                  <td>{cfg.llm_type}</td>
                                  <td>{cfg.script}</td>
                                  <td>
                                    {cfg.start_time} - {cfg.end_time}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  })}
                </section>
              )}
            </div>
          );
        })
      )}

      {/* --- Modals --- */}
      {showCompanyModal && (
        <CompanyEditModel
          initialData={companyToEdit}
          onClose={() => setShowCompanyModal(false)}
          onSave={handleSaveCompany}
          title={companyToEdit ? "Edit Company" : "Add Company"}
        />
      )}

      <AddCampaignModal
        isOpen={isCampaignModalOpen}
        onClose={() => setCampaignModalOpen(false)}
        companyId={selectedCompanyId}
        onSave={handleSaveCampaign}
        campaignToEdit={campaignToEdit}
      />

      {showDeleteModal && (
        <ConfirmDeleteModal
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
          onConfirm={handleConfirmDelete}
          itemName={deleteTarget?.name || "this campaign"}
        />
      )}
    </div>
  );
};

export default CompanyDashboard;
